import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, WOOD, cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { boxClad, pitchedRoof, seams } from './era2.ts';
import { IRON_BAND, IRON_STONE, TITAN_PLATES, TITAN_RUNE, TITAN_STONE, crack, rubbleAround, shellPlate, stain, type Wear } from './structures.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The forge, one look per level (docs/PROGRESION.md: the forge grows with the bastion):
 * - 1: an open-air smithy: a fieldstone hearth under a lean-to, bellows, an anvil on its stump, a quench tub;
 * - 2: a smith's shop: a timber-framed house open to the front, the hearth glowing inside, a stone chimney, a rack of blades;
 * - 3: a stone forge: an arched furnace mouth, a tall chimney with an iron cowl, two anvils and a grindstone;
 * - 4 (era III): the great forge on a dark plinth: twin chimneys, a furnace mouth carved as a dragon's head with
 *   its maw alight, dragon-red runes on the walls, crimson hangings and a blade cooling red on the anvil;
 * - 5 (era IV): the Vigil's forge: the same, its runes and ridge burning silver-white, the iron running white-hot,
 *   the Order's standard on the roof;
 * - 6 (era V): Somnhold's forge: of the titan's stone with shell plates and pale runes on a broader plinth, two
 *   dragon's heads breathing white fire (front and right flank), a monumental anvil on a block of the titan's stone,
 *   a shard of the Dream Beacon burning blue-white in a silver cage on the ridge.
 * Material 1 is timber and fieldstone, 2 dressed stone, 3 stone clad in iron (iron roof, straps). The table camera
 * looks from +X +Z: the fire and the work face that way. No point lights: glows and emissive only (docs/RENDIMIENTO.md).
 */

export interface ForgeOpts extends BaseOpts {
  level?: number;
  material?: number;
  wear?: Wear;
}

const PLINTH = stone('#5f5b55');
const DARK = stone('#3a3733');
const FIELD = stone('#77736c');
const ASHLAR = stone('#8b857a');
const QUOIN = stone('#a39d92');
const IRONSTONE = stone(IRON_STONE);
const SHINGLE = std('#4a2f22', { roughness: 0.95, flatShading: true });
const SLATE = stone('#3e3a36');
const IRON_ROOF = metal('#3d434b', { roughness: 0.42, metalness: 0.6, flatShading: true });
const FIRE = emis('#ffb070', '#ff5a10', 1.5);
const COALS = emis('#ff6a1a', '#ff3a00', 1.3);
const HOT = emis('#ffd0a0', '#ff6a1a', 1.6);
const RUNE = emis('#ffc0a8', '#ff4f3a', 1.4);
const CRIMSON = cloth('#7a1d22');
const EMBLEM = cloth('#d9dce3');
const HIDE = leather('#5a3a24');
const BLADE = metal('#c9d0d8', { roughness: 0.25 });
const WATER = std('#1d2a33', { roughness: 0.15, metalness: 0.2 });
const EARTH = stone('#4d463e');
const FLAG_A = stone('#6a655d');
const FLAG_B = stone('#5d5952');
const FLAG_C = stone('#757067');
const COAL = std('#1b1a1c', { roughness: 0.9, flatShading: true });
const MOLTEN = emis('#ffcf7a', '#ff6a10', 1.8);
const EYE = emis('#ffd2b8', '#ff4f3a', 2.0);
/** The Vigil's forge (level 5, era IV): runes and the ridge burn silver-white, the iron runs white-hot. */
const VIGIL = emis('#f2f6ff', '#bcd0ff', 1.6);
const WHITE_HOT = emis('#ffffff', '#cfdcff', 1.9);
const SILVER = metal('#c9d0da', { roughness: 0.3 });
/** Somnhold's forge (level 6, era V): the Dream Beacon's shard on the ridge. */
const LENS = emis('#f4f9ff', '#a8d0ff', 2.2);

const box = (p: THREE.Object3D, m: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
  const b = M(new THREE.BoxGeometry(w, h, d), m, p);
  b.position.set(x, y, z);
  b.rotation.y = ry;
  return b;
};
const cyl = (p: THREE.Object3D, m: THREE.Material, r0: number, r1: number, h: number, x: number, y: number, z: number, seg = 8): THREE.Mesh => {
  const c = M(new THREE.CylinderGeometry(r0, r1, h, seg), m, p);
  c.position.set(x, y, z);
  return c;
};

/** An anvil on its stump, horn to +X; `hot` lays a glowing blade across it. */
const anvil = (p: THREE.Object3D, x: number, z: number, ry: number, hot = false, s = 1): void => {
  const a = G(p, x, 0, z);
  a.rotation.y = ry;
  a.scale.setScalar(s);
  cyl(a, DWOOD, 0.075, 0.085, 0.16, 0, 0.08, 0, 7);
  box(a, DSTEEL, 0.1, 0.05, 0.07, 0, 0.185, 0);
  box(a, DSTEEL, 0.18, 0.04, 0.08, 0, 0.23, 0);
  const horn = M(new THREE.ConeGeometry(0.035, 0.1, 6), DSTEEL, a);
  horn.rotation.z = -Math.PI / 2;
  horn.position.set(0.135, 0.235, 0);
  if (hot) {
    box(a, HOT, 0.2, 0.012, 0.025, -0.01, 0.257, 0.005);
    glowSprite(a, '#ff7a2a', 0.22, V(0, 0.27, 0), 0.55);
  }
  // The hammer resting on the face.
  limb(a, V(-0.05, 0.255, 0.03), V(-0.13, 0.26, 0.09), 0.008, 0.008, WOOD, 5);
  box(a, DSTEEL, 0.035, 0.03, 0.03, -0.05, 0.262, 0.03);
};

/** Leather bellows lying toward the hearth (+X) at (x, z). */
const bellows = (p: THREE.Object3D, x: number, y: number, z: number, ry = 0): void => {
  const b = G(p, x, y, z);
  b.rotation.y = ry;
  const top = box(b, WOOD, 0.2, 0.015, 0.12, 0, 0.06, 0);
  top.rotation.z = 0.12;
  box(b, WOOD, 0.2, 0.015, 0.12, 0, 0, 0);
  const bag = M(new THREE.CylinderGeometry(0.055, 0.035, 0.18, 7), HIDE, b);
  bag.rotation.z = Math.PI / 2;
  bag.position.set(-0.01, 0.03, 0);
  bag.scale.z = 1.3;
  const nozzle = M(new THREE.ConeGeometry(0.018, 0.08, 5), DSTEEL, b);
  nozzle.rotation.z = -Math.PI / 2;
  nozzle.position.set(0.13, 0.025, 0);
  limb(b, V(-0.1, 0.06, 0), V(-0.18, 0.09, 0), 0.008, 0.008, WOOD, 4);
};

/** A quench tub: a staved barrel with dark water. */
const tub = (p: THREE.Object3D, x: number, z: number, r = 0.08): void => {
  cyl(p, WOOD, r, r * 0.9, r * 1.4, x, r * 0.7, z, 9);
  for (const y of [0.25, 0.75]) cyl(p, DSTEEL, r + 0.005, r + 0.005, 0.015, x, r * 1.4 * y, z, 9);
  cyl(p, WATER, r * 0.88, r * 0.88, 0.01, x, r * 1.4 - 0.008, z, 9);
};

/** A rack of finished blades leaning on a rail, crossguards and pommels showing. */
const bladeRack = (p: THREE.Object3D, x: number, z: number, ry: number, n = 3): void => {
  const r = G(p, x, 0, z);
  r.rotation.y = ry;
  for (const k of [-1, 1]) box(r, DWOOD, 0.03, 0.34, 0.03, k * 0.14, 0.17, 0);
  box(r, DWOOD, 0.32, 0.03, 0.03, 0, 0.3, 0);
  box(r, DWOOD, 0.32, 0.025, 0.06, 0, 0.02, 0.03);
  for (let i = 0; i < n; i++) {
    const bx = -0.09 + (i * 0.18) / Math.max(1, n - 1);
    const s = G(r, bx, 0.03, 0.05);
    s.rotation.x = -0.2;
    box(s, BLADE, 0.026, 0.3, 0.008, 0, 0.17, 0);
    box(s, DSTEEL, 0.08, 0.018, 0.02, 0, 0.32, 0);
    box(s, HIDE, 0.018, 0.06, 0.018, 0, 0.36, 0);
    M(new THREE.SphereGeometry(0.014, 6, 4), DSTEEL, s).position.set(0, 0.395, 0);
  }
};

/** A grindstone on its trestle, wheel facing +Z. */
const grindstone = (p: THREE.Object3D, x: number, z: number): void => {
  for (const k of [-1, 1]) limb(p, V(x + k * 0.06, 0, z), V(x + k * 0.05, 0.16, z), 0.01, 0.012, DWOOD, 4);
  const w = M(new THREE.CylinderGeometry(0.09, 0.09, 0.035, 12), stone('#9a948a'), p);
  w.rotation.z = Math.PI / 2;
  w.position.set(x, 0.17, z);
  limb(p, V(x - 0.07, 0.17, z), V(x + 0.08, 0.17, z), 0.008, 0.008, DSTEEL, 4);
};

/** A glowing hearth: a stone box with a bed of coals and a flame glow, at (x, z), `w` wide. */
const hearth = (p: THREE.Object3D, mat: THREE.Material, x: number, z: number, w: number, d: number, h: number): void => {
  box(p, mat, w, h, d, x, h / 2, z);
  box(p, DARK, w - 0.06, 0.02, d - 0.06, x, h + 0.005, z);
  for (let i = 0; i < 6; i++) M(new THREE.DodecahedronGeometry(0.028), COALS, p).position.set(x + (((i * 37) % 10) / 10 - 0.5) * (w - 0.1), h + 0.02, z + (((i * 53) % 10) / 10 - 0.5) * (d - 0.1));
  glowSprite(p, '#ff6a1a', w * 1.6, V(x, h + 0.08, z), 0.65);
};

/** The yard: a ring of flagstones in a few greys around a packed-earth floor, `r` across, `h` thick. */
const yard = (p: THREE.Object3D, r: number, h: number, seed: number): void => {
  const rng = mulberry32(seed);
  cyl(p, EARTH, r, r + 0.03, h, 0, h / 2, 0, 10);
  const greys = [FLAG_A, FLAG_B, FLAG_C];
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2 + rng() * 0.2;
    const d = r * (0.62 + rng() * 0.28);
    const f = box(p, greys[i % 3] as THREE.Material, 0.12 + rng() * 0.06, 0.016, 0.09 + rng() * 0.05, Math.cos(a) * d, h + 0.006, Math.sin(a) * d, -a + rng() * 0.4);
    f.rotation.y = -a + rng() * 0.4;
  }
};

/** A heap of coal and a stack of iron bars beside it. */
const stock = (p: THREE.Object3D, x: number, z: number, seed: number): void => {
  const rng = mulberry32(seed);
  for (let i = 0; i < 9; i++) M(new THREE.DodecahedronGeometry(0.025 + rng() * 0.02), COAL, p).position.set(x + (rng() - 0.5) * 0.12, 0.02 + rng() * 0.04, z + (rng() - 0.5) * 0.1);
  for (let i = 0; i < 4; i++) box(p, DSTEEL, 0.16, 0.022, 0.03, x + 0.14, 0.012 + (i > 2 ? 0.022 : 0), z - 0.04 + (i % 3) * 0.035);
};

/** Smoke wisps above a chimney top (static: baked once). */
const smoke = (p: THREE.Object3D, x: number, y: number, z: number, n = 3): void => {
  const m = new THREE.MeshBasicMaterial({ color: '#5a5550', transparent: true, opacity: 0.35, depthWrite: false });
  for (let i = 0; i < n; i++) {
    const s = M(new THREE.SphereGeometry(0.06 + i * 0.03, 7, 5), m, p);
    s.position.set(x + Math.sin(i * 1.7) * 0.05, y + 0.08 + i * 0.13, z + i * 0.02);
  }
};

const wallMat = (mat: number): THREE.Material => (mat === 1 ? FIELD : mat === 3 ? IRONSTONE : ASHLAR);
const roofMat = (mat: number): THREE.Material => (mat === 1 ? SHINGLE : mat === 3 ? IRON_ROOF : SLATE);

/** Level 1: the open-air smithy. */
const smithy = (g: THREE.Group, mat: number): number => {
  yard(g, 0.66, 0.05, 31);
  // The hearth against the back, the lean-to over it on two posts.
  hearth(g, wallMat(mat), -0.12, -0.22, 0.42, 0.3, 0.26);
  box(g, wallMat(mat), 0.42, 0.22, 0.08, -0.12, 0.37, -0.36);
  const roof = box(g, roofMat(mat), 0.8, 0.03, 0.62, -0.08, 0.66, -0.1);
  roof.rotation.x = 0.28;
  for (const x of [-0.44, 0.28]) {
    box(g, DWOOD, 0.045, 0.6, 0.045, x, 0.3, 0.16);
    box(g, DWOOD, 0.045, 0.78, 0.045, x, 0.39, -0.38);
  }
  box(g, DWOOD, 0.8, 0.04, 0.04, -0.08, 0.6, 0.16);
  if (mat === 3) for (const x of [-0.44, 0.28]) for (const y of [0.15, 0.45]) box(g, IRON_BAND, 0.06, 0.025, 0.06, x, y, 0.16);
  bellows(g, -0.45, 0.12, -0.2, 0);
  anvil(g, 0.2, 0.2, -0.4);
  tub(g, 0.42, -0.16);
  bladeRack(g, -0.36, 0.3, 0.5, 2);
  stock(g, -0.3, -0.48, 41);
  return 0.9;
};

/** Level 2: the smith's shop, timber framed, open to the front. */
const shop = (g: THREE.Group, mat: number): number => {
  yard(g, 0.7, 0.06, 32);
  const X = -0.1, Z = -0.12, W = 0.78, D = 0.56, H = 0.46;
  const wall = wallMat(mat);
  // Back and side walls; the front stands open on posts.
  box(g, wall, W, H, 0.06, X, 0.06 + H / 2, Z - D / 2);
  for (const k of [-1, 1]) box(g, wall, 0.06, H, D, X + (k * W) / 2, 0.06 + H / 2, Z);
  for (const x of [X - W / 2, X, X + W / 2]) box(g, DWOOD, 0.05, H, 0.05, x, 0.06 + H / 2, Z + D / 2);
  box(g, DWOOD, W + 0.06, 0.05, 0.05, X, 0.06 + H, Z + D / 2);
  if (mat === 1) {
    for (const k of [-1, 1]) {
      const br = box(g, DWOOD, 0.025, H * 1.05, 0.02, X + k * (W / 2 + 0.032), 0.06 + H / 2, Z, Math.PI / 2);
      br.rotation.x = k * 0.55;
    }
  }
  if (mat === 3) boxClad(g, X, Z, W, D, 0.06, 0.06 + H, [0.2, 0.44], 0, []);
  // The hearth inside, its glow spilling out of the open front.
  hearth(g, DARK, X - 0.12, Z - 0.1, 0.36, 0.26, 0.2);
  glowSprite(g, '#ff7a2a', 0.9, V(X, 0.3, Z + D / 2 + 0.05), 0.4);
  const roof = pitchedRoof(g, X, 0.06 + H + 0.02, Z, D + 0.06, 0.32, W + 0.06, roofMat(mat), wall);
  if (mat === 3) seams(roof.grp, D + 0.06, 0.32, W + 0.12, roof.angle);
  // The chimney at the back right corner.
  const cx = X + W / 2 - 0.12, cz = Z - D / 2 + 0.08;
  box(g, mat === 1 ? FIELD : QUOIN, 0.16, 0.6, 0.16, cx, 0.06 + H + 0.3, cz);
  box(g, DARK, 0.19, 0.04, 0.19, cx, 0.06 + H + 0.62, cz);
  smoke(g, cx, 0.06 + H + 0.62, cz);
  // The yard: anvil, quench tub, the rack of blades by the door.
  anvil(g, 0.32, 0.26, -0.5);
  tub(g, 0.45, -0.05);
  bladeRack(g, -0.38, 0.32, 0.25, 3);
  bellows(g, X - W / 2 + 0.12, 0.1, Z + 0.1, 0);
  stock(g, 0.4, -0.42, 42);
  return 1.15;
};

/** Level 3: the stone forge with its arched furnace and tall chimney. */
const stoneForge = (g: THREE.Group, mat: number): number => {
  yard(g, 0.72, 0.07, 33);
  const X = -0.08, Z = -0.12, W = 0.84, D = 0.62, H = 0.56;
  const wall = mat === 1 ? FIELD : mat === 3 ? IRONSTONE : ASHLAR;
  box(g, wall, W, H, D, X, 0.07 + H / 2, Z);
  // Quoins up the front corners.
  for (const k of [-1, 1]) for (let i = 0; i < 4; i++) box(g, QUOIN, i % 2 ? 0.09 : 0.13, 0.11, 0.06, X + k * (W / 2 - 0.04), 0.13 + i * 0.13, Z + D / 2 + 0.005);
  if (mat === 3) boxClad(g, X, Z, W, D, 0.07, 0.07 + H, [0.25, 0.55], 0.2, ['f', 'r']);
  // The arched furnace mouth on the front, alight.
  const front = Z + D / 2;
  box(g, BLACK, 0.34, 0.26, 0.02, X - 0.12, 0.2, front + 0.004);
  const arch = M(new THREE.CylinderGeometry(0.17, 0.17, 0.02, 12, 1, false, 0, Math.PI), BLACK, g);
  arch.rotation.set(Math.PI / 2, 0, -Math.PI / 2);
  arch.position.set(X - 0.12, 0.33, front + 0.004);
  box(g, FIRE, 0.28, 0.16, 0.012, X - 0.12, 0.17, front + 0.012);
  glowSprite(g, '#ff6a1a', 0.9, V(X - 0.12, 0.22, front + 0.1), 0.7);
  for (let i = 0; i < 7; i++) {
    const a = (i / 6) * Math.PI;
    const v = box(g, QUOIN, 0.06, 0.05, 0.05, X - 0.12 + Math.cos(a) * 0.2, 0.33 + Math.sin(a) * 0.2, front + 0.015);
    v.rotation.z = a - Math.PI / 2;
  }
  // A window to the right, its light warm.
  box(g, BLACK, 0.012, 0.12, 0.1, X + W / 2 + 0.004, 0.42, Z + 0.06);
  box(g, emis('#ff9a50', '#ff6a1a', 0.6), 0.012, 0.06, 0.07, X + W / 2 + 0.006, 0.4, Z + 0.06);
  const roof = pitchedRoof(g, X, 0.07 + H, Z, D + 0.04, 0.3, W + 0.04, roofMat(mat), wall);
  if (mat === 3) seams(roof.grp, D + 0.04, 0.3, W + 0.1, roof.angle);
  // The tall chimney, an iron cowl on top.
  const cx = X + W / 2 - 0.16, cz = Z - 0.04;
  box(g, QUOIN, 0.2, 0.76, 0.2, cx, 0.07 + H + 0.2, cz);
  box(g, DARK, 0.24, 0.05, 0.24, cx, 0.07 + H + 0.6, cz);
  for (const k of [-1, 1]) box(g, IRON_BAND, 0.03, 0.12, 0.03, cx + k * 0.08, 0.07 + H + 0.68, cz);
  box(g, IRON_BAND, 0.24, 0.025, 0.24, cx, 0.07 + H + 0.75, cz);
  smoke(g, cx, 0.07 + H + 0.75, cz, 4);
  // The yard.
  anvil(g, 0.32, 0.3, -0.5, true);
  anvil(g, -0.4, 0.33, 0.3, false, 0.85);
  grindstone(g, 0.48, -0.12);
  tub(g, 0.1, 0.38, 0.07);
  bladeRack(g, X - W / 2 - 0.02, Z + 0.05, Math.PI / 2, 3);
  stock(g, 0.42, -0.46, 44);
  return 1.55;
};

/** A dragon's head thrust out of a wall at (x, y, z), facing `ry` (0: +Z): brow, open jaws alight, burning eyes, horns. */
const dragonHead = (g: THREE.Group, x: number, y: number, z: number, ry: number, eye: THREE.Material, eyeGlow: string, fire: THREE.Material, fireGlow: string, glow: number): void => {
  const head = G(g, x, y, z);
  head.rotation.y = ry;
  box(head, DARK, 0.46, 0.4, 0.06, 0, 0.02, 0.0);
  box(head, DARK, 0.34, 0.12, 0.26, 0, 0.14, 0.12);
  const snout = box(head, DARK, 0.24, 0.08, 0.2, 0, 0.1, 0.32);
  snout.rotation.x = -0.18;
  for (const k of [-1, 1]) {
    box(head, eye, 0.06, 0.03, 0.02, k * 0.1, 0.17, 0.255);
    glowSprite(head, eyeGlow, 0.16, V(k * 0.1, 0.17, 0.28), 0.8);
    const horn = M(new THREE.ConeGeometry(0.04, 0.32, 5), DARK, head);
    horn.position.set(k * 0.15, 0.28, 0.02);
    horn.rotation.set(-0.9, 0, -k * 0.35);
    for (let i = 0; i < 3; i++) {
      const tooth = M(new THREE.ConeGeometry(0.016, 0.06, 4), QUOIN, head);
      tooth.position.set(k * (0.04 + i * 0.03), 0.04, 0.38 - i * 0.03);
      tooth.rotation.x = Math.PI;
    }
  }
  // The lower jaw hangs open: the fire roars in the maw.
  const jaw = box(head, DARK, 0.24, 0.05, 0.22, 0, -0.1, 0.24);
  jaw.rotation.x = 0.28;
  box(head, fire, 0.22, 0.12, 0.2, 0, -0.02, 0.18);
  glowSprite(head, fireGlow, glow, V(0, 0.0, 0.36), 0.85);
};

/** The roof of Somnhold's forge, shingled with the titan's shell plates (in `pitchedRoof`'s group frame). */
const shellRoof = (grp: THREE.Group, span: number, h: number, len: number, angle: number): void => {
  const half = span / 2, over = 0.06, thick = 0.035;
  const L = Math.hypot(half, h) + over;
  const r = 0.082;
  for (const side of [1, -1]) {
    const sl = G(grp, 0, h / 2 - (Math.sin(angle) * over) / 2 + (Math.cos(angle) * thick) / 2, side * (half / 2 + (Math.cos(angle) * over) / 2 + (Math.sin(angle) * thick) / 2));
    sl.rotation.x = side * angle;
    let row = 0;
    for (let z = -L / 2 + r; z <= L / 2 - r * 0.6; z += r * 1.5, row++) {
      for (let x = -len / 2 + r + (row % 2 ? r * 0.87 : 0); x <= len / 2 - r * 0.8; x += r * 1.74) {
        const p = M(new THREE.CylinderGeometry(r * 0.84, r, 0.026, 6), stone(TITAN_PLATES[(row + Math.round(x * 10)) % 3] as string), sl);
        p.position.set(x, thick / 2 + 0.01, z);
        p.rotation.y = Math.PI / 6;
      }
    }
  }
};

/** The monumental anvil of Somnhold's forge on a stepped block of the titan's stone, horn to +X, a blade white-hot on it. */
const greatAnvil = (p: THREE.Object3D, x: number, z: number, ry: number): void => {
  const a = G(p, x, 0, z);
  a.rotation.y = ry;
  const titan = stone(TITAN_STONE);
  box(a, titan, 0.36, 0.1, 0.28, 0, 0.05, 0);
  box(a, stone(TITAN_PLATES[2]), 0.28, 0.08, 0.2, 0, 0.14, 0);
  box(a, TITAN_RUNE, 0.3, 0.012, 0.012, 0, 0.1, 0.141);
  box(a, DSTEEL, 0.14, 0.1, 0.11, 0, 0.23, 0);
  box(a, DSTEEL, 0.3, 0.07, 0.13, 0, 0.31, 0);
  const horn = M(new THREE.ConeGeometry(0.06, 0.18, 6), DSTEEL, a);
  horn.rotation.z = -Math.PI / 2;
  horn.position.set(0.24, 0.315, 0);
  box(a, DSTEEL, 0.05, 0.05, 0.1, -0.17, 0.32, 0);
  box(a, WHITE_HOT, 0.32, 0.016, 0.04, -0.01, 0.353, 0.01);
  glowSprite(a, '#e8eeff', 0.42, V(0, 0.37, 0), 0.6);
  // The great hammer leaning on the block.
  limb(a, V(-0.2, 0.0, 0.12), V(-0.12, 0.36, 0.12), 0.012, 0.012, WOOD, 5);
  box(a, DSTEEL, 0.1, 0.06, 0.06, -0.12, 0.38, 0.12);
};

/**
 * Levels 4 to 6: the great forge (era III), the Vigil's (era IV, `tier` 5) and Somnhold's (era V, `tier` 6). The forge
 * of Somnhold is built of the titan's stone with shell plates and pale runes on a broader plinth; a second dragon's head
 * breathes from its right flank (the table camera sees both), a monumental anvil stands on a block of the titan's stone,
 * and a shard of the Dream Beacon burns blue-white in a silver cage on the ridge.
 */
const greatForge = (g: THREE.Group, mat: number, tier: 4 | 5 | 6): number => {
  const vigil = tier >= 5;
  const somn = tier === 6;
  const rune = somn ? TITAN_RUNE : vigil ? VIGIL : RUNE;
  const molten = vigil ? WHITE_HOT : MOLTEN;
  const runeGlow = somn ? '#a8d0ff' : vigil ? '#cfdcff' : '#ff4f3a';
  const fireGlow = somn ? '#dcecff' : vigil ? '#dfe8ff' : '#ff5a1a';
  const titan = stone(TITAN_STONE);
  // A dark octagonal plinth, runes burning in its rim (Somnhold's is broader, of the titan's stone, a second step).
  const pr = somn ? 0.8 : 0.72;
  cyl(g, somn ? titan : PLINTH, pr, pr + 0.04, 0.08, 0, 0.04, 0, 8).rotation.y = Math.PI / 8;
  cyl(g, DARK, 0.66, 0.68, 0.06, 0, 0.11, 0, 8).rotation.y = Math.PI / 8;
  for (let i = 0; i < 8; i += somn ? 1 : 2) {
    const a = (i * Math.PI) / 4;
    box(g, rune, 0.1, 0.03, 0.01, Math.sin(a) * (pr + 0.015), 0.045, Math.cos(a) * (pr + 0.015), a);
  }
  const y0 = 0.14;
  const X = -0.1, Z = -0.16, W = 0.9, D = 0.6, H = somn ? 0.66 : 0.6;
  const wall = somn ? titan : mat === 3 ? IRONSTONE : ASHLAR;
  const quoin = somn ? stone(TITAN_PLATES[2]) : QUOIN;
  box(g, wall, W, H, D, X, y0 + H / 2, Z);
  // Quoins and buttresses at the front corners, capped dark.
  for (const k of [-1, 1]) {
    box(g, quoin, 0.13, H * 0.92, 0.18, X + k * (W / 2 + 0.035), y0 + (H * 0.92) / 2, Z + D / 2 - 0.06);
    box(g, DARK, 0.16, 0.05, 0.21, X + k * (W / 2 + 0.035), y0 + H * 0.92 + 0.025, Z + D / 2 - 0.06);
  }
  if (mat === 3 && !somn) boxClad(g, X, Z, W, D, y0, y0 + H, [y0 + 0.22, y0 + 0.5], y0 + 0.16, ['f', 'r']);
  const front = Z + D / 2;
  const right = X + W / 2;
  // The furnace is a dragon's head thrust out of the wall; Somnhold's forge has a second one on its right flank.
  const hx = X - 0.05;
  const eye = vigil ? VIGIL : EYE;
  const eyeGlow = vigil ? '#cfdcff' : '#ff4f3a';
  const fire = vigil ? WHITE_HOT : FIRE;
  dragonHead(g, hx, y0 + 0.26, front, 0, eye, eyeGlow, fire, fireGlow, 1.2);
  if (somn) dragonHead(g, right, y0 + 0.26, Z + 0.02, Math.PI / 2, eye, eyeGlow, fire, fireGlow, 0.8);
  // Molten iron pours from the jaw down a channel to a mould in the yard.
  box(g, DARK, 0.1, 0.04, 0.42, hx, y0 + 0.02, front + 0.3);
  box(g, molten, 0.06, 0.012, 0.42, hx, y0 + 0.045, front + 0.3);
  box(g, DARK, 0.26, 0.06, 0.16, hx, y0 + 0.03, front + 0.56);
  box(g, molten, 0.2, 0.012, 0.1, hx, y0 + 0.062, front + 0.56);
  glowSprite(g, vigil ? '#e8eeff' : '#ff7a2a', 0.5, V(hx, y0 + 0.1, front + 0.5), 0.6);
  if (somn) {
    box(g, DARK, 0.34, 0.04, 0.1, right + 0.25, y0 + 0.02, Z + 0.02);
    box(g, molten, 0.34, 0.012, 0.06, right + 0.25, y0 + 0.045, Z + 0.02);
    glowSprite(g, '#e8eeff', 0.45, V(right + 0.3, y0 + 0.08, Z + 0.02), 0.6);
    // Shell plates along the foot of both faces the table sees, a line of runes over them.
    for (let i = 0; i < 4; i++) {
      const px = X - W / 2 + 0.1 + i * 0.1;
      if (i < 3) shellPlate(g, px, y0 + 0.1, front + 0.006, 0.055, 0, stone(TITAN_PLATES[i % 3] as string));
      shellPlate(g, X + W / 2 - 0.07 - i * 0.07, y0 + 0.42, front + 0.006, 0.04, 0, stone(TITAN_PLATES[(i + 1) % 3] as string));
    }
    for (const z of [Z - D / 2 + 0.08, Z + D / 2 - 0.12]) shellPlate(g, right + 0.006, y0 + 0.12, z, 0.05, Math.PI / 2, stone(TITAN_PLATES[1]));
    box(g, rune, W * 0.92, 0.014, 0.01, X, y0 + H - 0.08, front + 0.006);
    box(g, rune, 0.01, 0.014, D * 0.9, right + 0.006, y0 + H - 0.08, Z);
  } else {
    // Runes down the walls the table sees.
    for (const x of [X + 0.3, X + 0.38]) box(g, rune, 0.025, 0.18, 0.01, x, y0 + 0.4, front + 0.006);
    for (const z of [Z - 0.12, Z + 0.08]) box(g, rune, 0.01, 0.18, 0.025, right + 0.006, y0 + 0.38, z);
    glowSprite(g, runeGlow, 0.34, V(right + 0.05, y0 + 0.38, Z - 0.02), 0.45);
  }
  // Long crimson hangings from the eaves, the order's emblem on each.
  for (const k of [-1, 1]) {
    const bx = hx + k * 0.33;
    box(g, CRIMSON, 0.13, 0.46, 0.012, bx, y0 + H - 0.24, front + 0.014);
    const tail = M(new THREE.ConeGeometry(0.065, 0.08, 3), CRIMSON, g);
    tail.rotation.set(0, 0, Math.PI);
    tail.scale.z = 0.12;
    tail.position.set(bx, y0 + H - 0.51, front + 0.014);
    box(g, DWOOD, 0.17, 0.024, 0.026, bx, y0 + H - 0.005, front + 0.018);
    box(g, EMBLEM, 0.05, 0.05, 0.014, bx, y0 + H - 0.16, front + 0.022).rotation.z = Math.PI / 4;
  }
  // Roof, a red-hot ridge vent, twin chimneys crowned with spikes.
  const roof = pitchedRoof(g, X, y0 + H, Z, D + 0.06, 0.3, W + 0.06, somn ? titan : mat === 3 ? IRON_ROOF : SLATE, wall);
  if (somn) shellRoof(roof.grp, D + 0.06, 0.3, W + 0.06, roof.angle);
  else if (mat === 3) seams(roof.grp, D + 0.06, 0.3, W + 0.12, roof.angle);
  box(g, DARK, W * 0.5, 0.06, 0.1, X, y0 + H + 0.33, Z);
  box(g, somn ? TITAN_RUNE : vigil ? VIGIL : FIRE, W * 0.46, 0.02, 0.06, X, y0 + H + 0.36, Z);
  for (const k of [-1, 1]) {
    const cx = X + k * 0.32, cz = Z - D / 2 + 0.12;
    box(g, quoin, 0.18, 0.86, 0.18, cx, y0 + H + 0.25, cz);
    box(g, DARK, 0.24, 0.05, 0.24, cx, y0 + H + 0.7, cz);
    box(g, somn ? SILVER : IRON_BAND, 0.2, 0.03, 0.2, cx, y0 + H + 0.48, cz);
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2 + Math.PI / 4;
      M(new THREE.ConeGeometry(0.022, 0.1, 4), DSTEEL, g).position.set(cx + Math.sin(a) * 0.1, y0 + H + 0.77, cz + Math.cos(a) * 0.1);
    }
    glowSprite(g, vigil ? '#dfe8ff' : '#ff6a1a', 0.34, V(cx, y0 + H + 0.76, cz), 0.55);
    smoke(g, cx, y0 + H + 0.76, cz, 3);
  }
  let top = 1.7;
  if (somn) {
    // A shard of the Dream Beacon on the ridge: a little tower of the titan's stone, the lens in a silver cage.
    const by = y0 + H + 0.36;
    box(g, titan, 0.18, 0.2, 0.18, X, by + 0.1, Z);
    box(g, SILVER, 0.22, 0.03, 0.22, X, by + 0.21, Z);
    const lens = M(new THREE.IcosahedronGeometry(0.075, 0), LENS, g);
    lens.position.set(X, by + 0.33, Z);
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2 + Math.PI / 4;
      limb(g, V(X + Math.sin(a) * 0.09, by + 0.22, Z + Math.cos(a) * 0.09), V(X + Math.sin(a) * 0.1, by + 0.34, Z + Math.cos(a) * 0.1), 0.01, 0.01, SILVER, 4);
      limb(g, V(X + Math.sin(a) * 0.1, by + 0.34, Z + Math.cos(a) * 0.1), V(X, by + 0.5, Z), 0.01, 0.007, SILVER, 4);
    }
    M(new THREE.ConeGeometry(0.03, 0.14, 6), SILVER, g).position.set(X, by + 0.55, Z);
    glowSprite(g, '#cfe4ff', 1.1, V(X, by + 0.33, Z), 0.8);
    glowSprite(g, '#ffffff', 0.35, V(X, by + 0.33, Z), 0.9);
    top = by + 0.62;
  }
  if (vigil) {
    // The Order's standard on the ridge: a tall pole, a long crimson banner with the white emblem, a silver finial.
    const pole = right - 0.1;
    limb(g, V(pole, y0 + H + 0.1, Z), V(pole, y0 + H + 0.95, Z), 0.012, 0.014, DWOOD, 5);
    M(new THREE.SphereGeometry(0.025, 8, 6), SILVER, g).position.set(pole, y0 + H + 0.97, Z);
    box(g, CRIMSON, 0.012, 0.36, 0.16, pole + 0.012, y0 + H + 0.74, Z + 0.09);
    box(g, EMBLEM, 0.014, 0.05, 0.05, pole + 0.02, y0 + H + 0.8, Z + 0.09).rotation.x = Math.PI / 4;
  }
  if (somn) {
    // The monumental anvil, a blade white-hot on its face; the rack and the stock.
    greatAnvil(g, 0.44, 0.42, -0.5);
    grindstone(g, -0.55, 0.34);
    bladeRack(g, X - W / 2 - 0.08, Z - 0.02, Math.PI / 2, 4);
    stock(g, 0.3, -0.58, 43);
    return Math.max(top, 1.95);
  }
  // The yard: the great anvil with a blade cooling red, a tub, the rack, a grindstone, the stock.
  anvil(g, 0.42, 0.3, -0.5, true, 1.15);
  tub(g, 0.56, -0.04, 0.08);
  grindstone(g, -0.5, 0.3);
  bladeRack(g, X - W / 2 - 0.08, Z - 0.02, Math.PI / 2, 4);
  stock(g, 0.42, -0.5, 43);
  return top;
};

export const forge = (o?: ForgeOpts): Model => {
  const level = Math.min(6, Math.max(1, o?.level ?? 1));
  const mat = Math.min(3, Math.max(1, o?.material ?? 1));
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.75, 72);
  const { g } = c;
  const height = level === 1 ? smithy(g, mat) : level === 2 ? shop(g, mat) : level === 3 ? stoneForge(g, mat) : greatForge(g, mat, Math.min(6, level) as 4 | 5 | 6);
  if (wear > 0) {
    const wr = mulberry32(720 + level * 7 + mat * 3 + wear);
    const n = wear === 2 ? 5 : 3;
    for (let i = 0; i < n; i++) {
      const front = i % 2 === 0;
      if (front) crack(g, -0.4 + wr() * 0.6, 0.2 + wr() * 0.25, 0.22, 0, wear === 2 ? 0.36 : 0.26, wr);
      else crack(g, 0.33, 0.2 + wr() * 0.25, -0.3 + wr() * 0.3, Math.PI / 2, wear === 2 ? 0.36 : 0.26, wr);
    }
    stain(g, 0.1, 0.2, 0.231, 0, 0.12, wr, '#26221f');
    rubbleAround(g, wear === 2 ? 10 : 5, 0.58, wr, ['#6d675e', '#625c54', '#77716a'], -0.05, -0.1);
  }
  return done(c, height);
};
